import { PropsWithChildren, Ref, forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import { useSearchParams } from 'react-router'

import { pageSize as defaultPageSize } from 'app/application/constants'
import { Paging } from 'app/common/types'

import { PageControls, Table } from 'components'
import { TableProps } from 'components/Table'

export interface PagedTableProps<T> extends PropsWithChildren, TableProps<T> {
  pageSize?: number
  useSearchParamsPaging?: boolean
}

export interface PagedTableFunctions {
  setPage: (page: number) => void
}

const getInitialPaging = <T,>(data: T[], pageSize: number, page = '1') => ({
  page: !isNaN(Number(page)) ? +page : 1,
  pageSize,
  totalPages: Math.ceil(data.length / pageSize),
})

const PagedTableInner = <T,>(
  {
    data,
    pageSize = defaultPageSize,
    useSearchParamsPaging = false,
    ...tableProps
  }: PagedTableProps<T>,
  ref: Ref<PagedTableFunctions>,
) => {
  const [searchParams, setSearchParams] = useSearchParams()

  const [paging, setPaging] = useState<Paging>(
    getInitialPaging(data, pageSize, searchParams.get('page') ?? '1'),
  )

  useEffect(
    () =>
      setPaging((prev) => ({ ...prev, pageSize, totalPages: Math.ceil(data.length / pageSize) })),
    [data, pageSize],
  )
  useEffect(() => {
    const pageParam = searchParams.get('page')
    const page = pageParam && !isNaN(Number(pageParam)) ? +pageParam : 1

    setPaging((prev) =>
      prev.page === page ? prev : { ...prev, page: Math.max(1, Math.min(+page, prev.totalPages)) },
    )
  }, [searchParams])

  const handlePageChange = (page: number) => {
    if (useSearchParamsPaging) {
      setSearchParams((prev) => {
        if (page !== paging.page) {
          prev.set('page', page.toString())
        }
        return prev
      })
    } else {
      setPaging((prev) => ({ ...prev, page }))
    }
  }

  const setPage = (page: number) => handlePageChange(page)

  useImperativeHandle(ref, () => ({
    setPage,
  }))

  return (
    <>
      <Table.Table
        data={data.slice((paging.page - 1) * paging.pageSize, paging.page * paging.pageSize)}
        {...tableProps}
      />
      <PageControls
        currentPage={paging.page}
        onPageChange={handlePageChange}
        totalPages={paging.totalPages}
      />
    </>
  )
}

// Compose with forwardRef and re-apply generic signature
const PagedTable = forwardRef(PagedTableInner) as <T>(
  props: PagedTableProps<T> & { ref?: React.Ref<PagedTableFunctions> },
) => ReturnType<typeof PagedTableInner>

export default PagedTable
